import React, { useMemo, useState } from 'react';
import type { LinkedAmqpOutletsResponse, LinkedAmqpOutlet } from '../../../../../services/transformerAmqpService';
import { linkOutletToCable, unlinkOutletFromCable } from '../../../../../services/transformerAmqpService';
import type { Abgangsleitung } from '../../../../../types/network';
import { OutletDetailModal } from './OutletDetailModal';
import { parseUtcTimestamp } from '../../../../../utils/timeUtils';
import { fmt } from '../../../../../utils/numberFormat';
import { NOMINAL_VOLTAGE_LN_V } from '../../../../../utils/electricalConstants';

type PhaseKey = 'L1' | 'L2' | 'L3' | 'N';

type PhaseMetric = {
  value: number;
  unit: string;
} | null;

export type PhaseSummary = Record<PhaseKey, {
  current: PhaseMetric;
  activePower: PhaseMetric;
  reactivePower: PhaseMetric;
  apparentPower: PhaseMetric;
}>;

interface Props {
  linkedData: LinkedAmqpOutletsResponse | null;
  isLoading: boolean;
  error: string | null;
  phaseSummary: PhaseSummary;
  trafoId: string;
  connectedLines: Abgangsleitung[];
  onReload: () => void;
}

const getOutletLabel = (outlet: LinkedAmqpOutlet) => {
  const name = outlet.name || outlet.portName || outlet.valuePrefix || 'Outlet';
  const slot = outlet.slot !== null ? `Slot ${outlet.slot}` : null;
  return slot ? `${name} (${slot})` : name;
};

const formatValue = (value: number) => {
  if (Number.isNaN(value)) return '—';
  return fmt(value, 2);
};

const PhaseTable = ({ phaseSummary, highlightPhase }: { phaseSummary: PhaseSummary; highlightPhase: PhaseKey | null }) => {
  const phases: PhaseKey[] = ['L1', 'L2', 'L3', 'N'];

  const rows: { label: string; color: string; key: 'current' | 'activePower' | 'reactivePower' | 'apparentPower' }[] = [
    { label: 'Strom', color: 'text-cyan-500 dark:text-cyan-400', key: 'current' },
    { label: 'P', color: 'text-blue-500 dark:text-blue-400', key: 'activePower' },
    { label: 'Q', color: 'text-violet-500 dark:text-violet-400', key: 'reactivePower' },
    { label: 'S', color: 'text-amber-500 dark:text-amber-400', key: 'apparentPower' },
  ];

  return (
    <div className="rounded-lg border border-gray-200 dark:border-primary-600 overflow-hidden">
      {/* Phase header row */}
      <div className="grid grid-cols-[auto_repeat(4,1fr)] bg-gray-50 dark:bg-primary-700 border-b border-gray-200 dark:border-primary-600">
        <div className="w-10" />
        {phases.map(phase => (
          <div key={phase} className="px-2 py-2 text-center">
            <span className={`text-xs font-bold ${highlightPhase === phase ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500 dark:text-primary-300'}`}>
              {phase}
            </span>
            {highlightPhase === phase && (
              <div className="text-[9px] text-blue-500 dark:text-blue-400 font-medium">max</div>
            )}
          </div>
        ))}
      </div>

      {/* Metric rows */}
      {rows.map((row, rowIdx) => (
        <div
          key={row.key}
          className={`grid grid-cols-[auto_repeat(4,1fr)] ${
            rowIdx < rows.length - 1 ? 'border-b border-gray-100 dark:border-primary-700' : ''
          }`}
        >
          <div className={`w-10 flex items-center justify-center text-[11px] font-semibold ${row.color}`}>
            {row.label}
          </div>
          {phases.map(phase => {
            const metric = phaseSummary[phase][row.key];
            const isHighCol = highlightPhase === phase;
            return (
              <div
                key={phase}
                className={`px-2 py-1.5 text-center ${isHighCol ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
              >
                {metric ? (
                  <>
                    <div className="text-[13px] font-semibold tabular-nums text-gray-900 dark:text-white leading-tight">
                      {formatValue(metric.value)}
                    </div>
                    <div className="text-[10px] text-gray-400 dark:text-primary-500">{metric.unit}</div>
                  </>
                ) : (
                  <div className="text-sm text-gray-300 dark:text-primary-600">—</div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const normalizeUnit = (unit?: string | null) => (unit ?? '').trim().toLowerCase();

const convertPowerToKw = (value: number, unit?: string | null) => {
  const normalized = normalizeUnit(unit);
  if (!normalized || normalized === 'w') return value / 1000;
  if (normalized === 'kw') return value;
  if (normalized === 'mw') return value * 1000;
  return value / 1000;
};

const isTotalPowerMeasurement = (measurement: { measurementType?: string | null; iecName?: string | null }) => {
  const type = (measurement.measurementType ?? '').toLowerCase();
  if (type === 'power_total') return true;
  const iec = (measurement.iecName ?? '').toLowerCase();
  return iec.includes('totw');
};

const isTotalReactiveMeasurement = (measurement: { measurementType?: string | null; iecName?: string | null }) => {
  const type = (measurement.measurementType ?? '').toLowerCase();
  if (type === 'reactive_power_total') return true;
  const iec = (measurement.iecName ?? '').toLowerCase();
  return iec.includes('totvar');
};

const isTotalApparentMeasurement = (measurement: { measurementType?: string | null; iecName?: string | null }) => {
  const type = (measurement.measurementType ?? '').toLowerCase();
  if (type === 'apparent_power_total') return true;
  const iec = (measurement.iecName ?? '').toLowerCase();
  return iec.includes('totva') && !iec.includes('totvar');
};

const convertReactiveToKvar = (value: number, unit?: string | null) => {
  const normalized = normalizeUnit(unit);
  if (!normalized || normalized === 'var') return value / 1000;
  if (normalized === 'kvar') return value;
  if (normalized === 'mvar') return value * 1000;
  return value / 1000;
};

const convertApparentToKva = (value: number, unit?: string | null) => {
  const normalized = normalizeUnit(unit);
  if (!normalized || normalized === 'va') return value / 1000;
  if (normalized === 'kva') return value;
  if (normalized === 'mva') return value * 1000;
  return value / 1000;
};

const isTrafoOutlet = (outlet: LinkedAmqpOutlet) => {
  // Trafo outlet has no linked cable and is either marked as trafo or has no linkedCableId
  return !outlet.linkedCableId && (outlet.deviceFamily ?? '').toLowerCase() === 'trafo';
};

// Deviation reference Un = 230 V (EN 50160); measured volts need no pu conversion.
const VOLTAGE_NOMINAL = NOMINAL_VOLTAGE_LN_V;

const VoltagePhaseDisplay = ({ l1V, l2V, l3V }: { l1V: number | null; l2V: number | null; l3V: number | null }) => {
  const phases = [
    { label: 'L1', value: l1V },
    { label: 'L2', value: l2V },
    { label: 'L3', value: l3V },
  ];

  return (
    <div className="grid grid-cols-3 gap-2">
      {phases.map(({ label, value }) => {
        const offset = value != null ? value - VOLTAGE_NOMINAL : null;
        return (
          <div key={label} className="rounded-lg p-2.5 bg-gray-50 dark:bg-primary-700/50 border border-gray-200 dark:border-primary-600">
            <div className="text-[10px] font-bold uppercase tracking-wide mb-1 text-gray-500 dark:text-primary-300">{label}</div>
            <div className="text-[17px] font-bold tabular-nums leading-tight text-gray-900 dark:text-white">
              {value != null ? fmt(value, 1) : '—'}
            </div>
            <div className="text-[10px] text-gray-400 dark:text-primary-500 leading-none mb-1">V</div>
            {offset != null && (
              <div className="text-[11px] text-gray-500 dark:text-primary-300 tabular-nums">
                {offset >= 0 ? '+' : ''}{fmt(offset, 1)} V
              </div>
            )}
            <div className="text-[9px] text-gray-400 dark:text-primary-500 mt-0.5">Ref. {NOMINAL_VOLTAGE_LN_V} V</div>
          </div>
        );
      })}
    </div>
  );
};

export const LemonbeatMeasurementsSection: React.FC<Props> = ({
  linkedData,
  isLoading,
  error,
  phaseSummary,
  trafoId,
  connectedLines,
  onReload
}) => {
  const [expandedOutletId, setExpandedOutletId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showUnlinkedOutlets, setShowUnlinkedOutlets] = useState(false);
  const [detailOutlet, setDetailOutlet] = useState<LinkedAmqpOutlet | null>(null);

  const highlightPhase = useMemo(() => {
    const entries: Array<{ phase: PhaseKey; value: number }> = [
      { phase: 'L1', value: phaseSummary.L1.current?.value ?? 0 },
      { phase: 'L2', value: phaseSummary.L2.current?.value ?? 0 },
      { phase: 'L3', value: phaseSummary.L3.current?.value ?? 0 },
      { phase: 'N', value: phaseSummary.N.current?.value ?? 0 }
    ];

    const maxEntry = entries.reduce((best, entry) => (entry.value > best.value ? entry : best), entries[0]);
    return maxEntry.value > 0 ? maxEntry.phase : null;
  }, [phaseSummary]);

  const outletsWithPowerData = useMemo(() => {
    const outlets = linkedData?.outlets ?? [];
    return outlets.map(outlet => {
      const measurements = outlet.measurements ?? [];

      const activePowerMeasurement = measurements.find(m => isTotalPowerMeasurement(m));
      const reactivePowerMeasurement = measurements.find(m => isTotalReactiveMeasurement(m));
      const apparentPowerMeasurement = measurements.find(m => isTotalApparentMeasurement(m));

      const activePowerKw = activePowerMeasurement
        ? convertPowerToKw(Number(activePowerMeasurement.value), activePowerMeasurement.unit ?? activePowerMeasurement.einheit)
        : null;
      const reactivePowerKvar = reactivePowerMeasurement
        ? convertReactiveToKvar(Number(reactivePowerMeasurement.value), reactivePowerMeasurement.unit ?? reactivePowerMeasurement.einheit)
        : null;
      const apparentPowerKva = apparentPowerMeasurement
        ? convertApparentToKva(Number(apparentPowerMeasurement.value), apparentPowerMeasurement.unit ?? apparentPowerMeasurement.einheit)
        : null;

      return { outlet, activePowerKw, reactivePowerKvar, apparentPowerKva };
    });
  }, [linkedData]);

  // Get cables that are already linked to an outlet
  const linkedCableIds = useMemo(() => {
    const ids = new Set<string>();
    for (const outlet of linkedData?.outlets ?? []) {
      if (outlet.linkedCableId) {
        ids.add(outlet.linkedCableId);
      }
    }
    return ids;
  }, [linkedData]);

  // Available cables for linking (not already linked)
  const availableCables = useMemo(() => {
    return connectedLines.filter(cable => !linkedCableIds.has(cable.id));
  }, [connectedLines, linkedCableIds]);

  // Extract per-phase voltages from the trafo outlet — same priority as TrafostationDetails.tsx
  const trafoVoltages = useMemo(() => {
    const outlets = linkedData?.outlets ?? [];
    const trafoOutlet =
      (linkedData?.trafoOutletId ? outlets.find(o => o.id === linkedData.trafoOutletId) : null)
      ?? outlets.find(o => (o.deviceFamily ?? '').toLowerCase() === 'trafo')
      ?? outlets.find(o => !o.linkedCableId)
      ?? null;
    if (!trafoOutlet) return null;
    const measurements = trafoOutlet.measurements ?? [];
    const getVoltageV = (type: string) => {
      const m = measurements.find(m => (m.measurementType ?? '').toLowerCase() === type);
      return m != null ? Number(m.value) : null;
    };
    const l1V = getVoltageV('voltage_phase_a');
    const l2V = getVoltageV('voltage_phase_b');
    const l3V = getVoltageV('voltage_phase_c');
    return (l1V != null || l2V != null || l3V != null) ? { l1V, l2V, l3V } : null;
  }, [linkedData]);

  const handleLink = async (outletId: string, cableId: string) => {
    setIsSaving(true);
    try {
      await linkOutletToCable(trafoId, outletId, cableId);
      setExpandedOutletId(null);
      await onReload();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnlink = async (outletId: string) => {
    setIsSaving(true);
    try {
      await unlinkOutletFromCable(trafoId, outletId);
      await onReload();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const getLinkedCableName = (cableId: string) => {
    const cable = connectedLines.find(c => c.id === cableId);
    return cable?.name || cableId;
  };

  return (
    <div className="bg-white dark:bg-primary-800 rounded-md shadow-sm border border-gray-200 dark:border-primary-600 overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 dark:bg-primary-700 border-b border-gray-200 dark:border-primary-600 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4 text-gray-700 dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <h3 className="text-xs font-bold text-gray-900 dark:text-white">Trafo Messungen</h3>
        </div>
        {linkedData ? (
          <span className="text-xs text-green-600 dark:text-green-400">Daten vorhanden</span>
        ) : (
          <span className="text-xs text-gray-500 dark:text-primary-300">Keine Daten</span>
        )}
      </div>

      <div className="p-4 space-y-4">
        {isLoading && (
          <div className="text-sm text-gray-500 dark:text-primary-300">Lade Trafo Messungen...</div>
        )}

        {!isLoading && !linkedData && !error && (
          <div className="text-sm text-gray-500 dark:text-primary-300">
            Keine Messdaten zu dieser Trafostation gefunden.
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
        )}

        {linkedData && !isLoading && (
          <>
            <div className="text-xs text-gray-500 dark:text-primary-300 flex flex-wrap gap-3">
              <span>SGTIN: {linkedData.deviceSgtin}</span>
              {linkedData.lastMessageAt && <span>Letzte Messung: {parseUtcTimestamp(linkedData.lastMessageAt).toLocaleString('de-DE')}</span>}
            </div>

            {trafoVoltages && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-primary-300 mb-2">
                  Spannung pro Phase (LV-Sammelschiene)
                </div>
                <VoltagePhaseDisplay l1V={trafoVoltages.l1V} l2V={trafoVoltages.l2V} l3V={trafoVoltages.l3V} />
              </div>
            )}

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-primary-300 mb-2">
                Strom & Leistung (Phasen)
              </div>
              <PhaseTable phaseSummary={phaseSummary} highlightPhase={highlightPhase} />
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-primary-300 mb-2">
                Zugeordnete Outlets
              </div>
              <div className="space-y-2">
                {outletsWithPowerData
                  .filter(({ outlet }) => isTrafoOutlet(outlet) || !!outlet.linkedCableId)
                  .map(({ outlet, activePowerKw, reactivePowerKvar, apparentPowerKva }) => {
                    const isTrafo = isTrafoOutlet(outlet);

                    return (
                      <div key={outlet.id} className="border border-gray-200 dark:border-primary-600 rounded-md overflow-hidden">
                        <div className="px-3 py-2">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0">
                              <span className="text-sm font-medium text-gray-800 dark:text-white">
                                {getOutletLabel(outlet)}
                              </span>
                              {isTrafo && (
                                <span className="text-[10px] bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300 px-1.5 py-0.5 rounded ml-1.5 inline-block align-middle">
                                  Trafo
                                </span>
                              )}
                              {!isTrafo && outlet.linkedCableId && (
                                <div className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                                  {getLinkedCableName(outlet.linkedCableId)}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => setDetailOutlet(outlet)}
                                className="text-xs px-2 py-1 rounded border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                              >
                                Detail
                              </button>
                              {!isTrafo && outlet.linkedCableId && (
                                <button
                                  onClick={() => handleUnlink(outlet.id)}
                                  disabled={isSaving}
                                  className="text-xs px-2 py-1 rounded border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                                >
                                  Entfernen
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mt-1">
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-2 text-center">
                              <span className="block text-[10px] text-blue-600 dark:text-blue-400 font-medium">Wirkleistung</span>
                              <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
                                {activePowerKw !== null ? `${formatValue(activePowerKw)} kW` : '—'}
                              </span>
                            </div>
                            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-md p-2 text-center">
                              <span className="block text-[10px] text-purple-600 dark:text-purple-400 font-medium">Blindleistung</span>
                              <span className="text-sm font-bold text-purple-700 dark:text-purple-300">
                                {reactivePowerKvar !== null ? `${formatValue(reactivePowerKvar)} kvar` : '—'}
                              </span>
                            </div>
                            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-md p-2 text-center">
                              <span className="block text-[10px] text-amber-600 dark:text-amber-400 font-medium">Scheinleistung</span>
                              <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                                {apparentPowerKva !== null ? `${formatValue(apparentPowerKva)} kVA` : '—'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                {outletsWithPowerData.filter(({ outlet }) => isTrafoOutlet(outlet) || !!outlet.linkedCableId).length === 0 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                    Keine zugeordneten Outlets
                  </div>
                )}
              </div>

              {/* Unlinked Outlets Dropdown */}
              {outletsWithPowerData.filter(({ outlet }) => !isTrafoOutlet(outlet) && !outlet.linkedCableId).length > 0 && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowUnlinkedOutlets(!showUnlinkedOutlets)}
                    className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
                  >
                    <svg
                      className={`w-4 h-4 transition-transform ${showUnlinkedOutlets ? 'rotate-90' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                    <span>Nicht zugeordnete Outlets ({outletsWithPowerData.filter(({ outlet }) => !isTrafoOutlet(outlet) && !outlet.linkedCableId).length})</span>
                  </button>

                  {showUnlinkedOutlets && (
                    <div className="mt-2 space-y-2">
                      {outletsWithPowerData
                        .filter(({ outlet }) => !isTrafoOutlet(outlet) && !outlet.linkedCableId)
                        .map(({ outlet, activePowerKw, reactivePowerKvar, apparentPowerKva }) => {
                          const isExpanded = expandedOutletId === outlet.id;

                          return (
                            <div key={outlet.id} className="border border-gray-200 dark:border-primary-600 rounded-md overflow-hidden bg-gray-50 dark:bg-primary-700/50">
                              <div className="px-3 py-2">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {getOutletLabel(outlet)}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setDetailOutlet(outlet)}
                                      className="text-xs px-2 py-1 rounded border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                    >
                                      Detail
                                    </button>
                                    <button
                                      onClick={() => setExpandedOutletId(isExpanded ? null : outlet.id)}
                                      disabled={isSaving || availableCables.length === 0}
                                      className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-primary-500 text-gray-700 dark:text-primary-200 hover:bg-gray-100 dark:hover:bg-primary-600 disabled:opacity-50"
                                    >
                                      Zuordnen
                                    </button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mt-1">
                                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-2 text-center">
                                    <span className="block text-[10px] text-blue-600 dark:text-blue-400 font-medium">Wirkleistung</span>
                                    <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
                                      {activePowerKw !== null ? `${formatValue(activePowerKw)} kW` : '—'}
                                    </span>
                                  </div>
                                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-md p-2 text-center">
                                    <span className="block text-[10px] text-purple-600 dark:text-purple-400 font-medium">Blindleistung</span>
                                    <span className="text-sm font-bold text-purple-700 dark:text-purple-300">
                                      {reactivePowerKvar !== null ? `${formatValue(reactivePowerKvar)} kvar` : '—'}
                                    </span>
                                  </div>
                                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-md p-2 text-center">
                                    <span className="block text-[10px] text-amber-600 dark:text-amber-400 font-medium">Scheinleistung</span>
                                    <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                                      {apparentPowerKva !== null ? `${formatValue(apparentPowerKva)} kVA` : '—'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Cable selection dropdown */}
                              {isExpanded && (
                                <div className="px-3 py-2 bg-gray-100 dark:bg-primary-700 border-t border-gray-200 dark:border-primary-600">
                                  <div className="text-xs text-gray-500 dark:text-primary-400 mb-2">
                                    Kabel auswählen:
                                  </div>
                                  <div className="space-y-1">
                                    {availableCables.map(cable => (
                                      <button
                                        key={cable.id}
                                        onClick={() => handleLink(outlet.id, cable.id)}
                                        disabled={isSaving}
                                        className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-gray-200 dark:hover:bg-primary-600 text-gray-800 dark:text-primary-100 disabled:opacity-50"
                                      >
                                        {cable.name || cable.id}
                                      </button>
                                    ))}
                                    {availableCables.length === 0 && (
                                      <div className="text-xs text-gray-500 dark:text-primary-400">
                                        Keine verfügbaren Kabel
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {detailOutlet && (
        <OutletDetailModal
          isOpen={!!detailOutlet}
          outlet={detailOutlet}
          trafoId={trafoId}
          onClose={() => setDetailOutlet(null)}
        />
      )}
    </div>
  );
};
